import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogIn, UserPlus, MailCheck, Send } from "lucide-react";

const HERO = "https://images.pexels.com/photos/7951657/pexels-photo-7951657.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return fallback;
}

export function LoginDialog({ open, onOpenChange, onSignedIn, defaultMode = "signin" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => { if (open) { setMode(defaultMode); setSentTo(null); } }, [open, defaultMode]);

  const signUp = mode === "signup";
  const forgot = mode === "forgot";

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (forgot) {
        if (!email.trim()) { setBusy(false); return; }
        await api.forgotPassword(email.trim(), window.location.origin);
        setSentTo(email.trim());
        setBusy(false);
        return;
      } else if (signUp) {
        if (!email.trim() || !password) { setBusy(false); return; }
        const u = await register(name.trim(), email.trim(), password);
        toast.success(`Welcome, ${u.name?.split(" ")[0] || "friend"}! Your family tree is ready.`);
      } else {
        if (!username.trim() || !password) { setBusy(false); return; }
        const u = await login(username.trim(), password);
        toast.success(`Welcome back, ${u.name || u.username}`);
      }
      setName(""); setEmail(""); setUsername(""); setPassword("");
      onSignedIn?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errText(err, forgot ? "Could not send the reset email" : signUp ? "Could not create your account" : "Invalid email or password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none gap-0 overflow-hidden border-0 p-0 sm:rounded-none [&>button]:right-4 [&>button]:top-4 [&>button]:z-20 [&>button]:rounded-full [&>button]:bg-white/90 [&>button]:p-2 [&>button]:text-[#082D4A] [&>button]:opacity-100 [&>button]:shadow [&>button>svg]:h-5 [&>button>svg]:w-5"
        style={{ width: "100vw", height: "100vh", maxHeight: "100vh" }}
        data-testid="login-dialog"
      >
        <DialogTitle className="sr-only">{signUp ? "Create your family tree" : "Sign in to Where Am I From"}</DialogTitle>
        <DialogDescription className="sr-only">Create a private family tree, or sign in to your existing family space.</DialogDescription>

        <div className="grid h-full w-full lg:grid-cols-2">
          {/* Hero */}
          <div className="relative hidden lg:block">
            <img src={HERO} alt="Family through the years" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[#082D4A]/60" />
            <div className="absolute left-10 top-8 flex items-center gap-2.5 text-white">
              <img src="/logo-mark.png" alt="" className="h-10 w-10 rounded-lg bg-white/95 p-1" />
              <span className="font-brand text-lg">Where Am i From<span className="text-[#D4A017]">.net</span></span>
            </div>
            <div className="absolute bottom-14 left-10 right-10 text-white">
              <h1 className="font-serif text-5xl font-bold leading-[1.05] lg:text-6xl">Discover the roots<br />of your story</h1>
              <p className="mt-4 max-w-md text-base leading-relaxed text-white/85">
                Every family gets its own private tree. Add relatives, bring memories to life, and share your story across the generations.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="flex h-full items-center justify-center overflow-y-auto bg-[#F4F6F8] p-8">
            <div className="w-full max-w-sm">
              <div className="mb-8 flex items-center gap-2.5 lg:hidden">
                <img src="/logo-mark.png" alt="" className="h-10 w-10 object-contain" />
                <span className="font-brand text-lg text-[#082D4A]">Where Am i From<span className="text-[#8A6A0C]">.net</span></span>
              </div>

              {forgot && sentTo ? (
                <div data-testid="forgot-sent">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#007F6B]/10 text-[#007F6B]"><MailCheck size={28} /></div>
                  <h2 className="font-serif text-3xl font-bold text-[#082D4A]">Check your inbox</h2>
                  <p className="mt-2 text-sm text-[#64748B]">
                    If an account exists for <span className="font-medium text-[#082D4A]">{sentTo}</span>, we've sent a link to reset your password. It expires in 1 hour.
                  </p>
                  <button type="button" onClick={() => { setMode("signin"); setSentTo(null); }} data-testid="back-to-signin" className="mt-8 w-full text-sm font-medium text-[#082D4A] hover:underline">Back to sign in</button>
                </div>
              ) : forgot ? (
                <>
                  <h2 className="font-serif text-3xl font-bold text-[#082D4A]">Reset your password</h2>
                  <p className="mt-2 text-sm text-[#64748B]">Enter the email for your family space and we'll send you a secure link to set a new password.</p>
                  <form onSubmit={submit} className="mt-8 space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="forgot-email">Email</Label>
                      <Input id="forgot-email" name="email" autoComplete="email" data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" placeholder="you@example.com" autoFocus />
                    </div>
                    <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="h-11 w-full rounded-full bg-[#D4A017] text-base font-semibold text-[#082D4A] hover:bg-[#B0850F]">
                      <Send size={16} className="mr-2" />
                      {busy ? "Sending…" : "Send reset link"}
                    </Button>
                  </form>
                  <button type="button" onClick={() => setMode("signin")} data-testid="back-to-signin" className="mt-6 w-full text-sm font-medium text-[#082D4A] hover:underline">Back to sign in</button>
                </>
              ) : (
              <>
              <h2 className="font-serif text-3xl font-bold text-[#082D4A]">{signUp ? "Create your family tree" : "Welcome back"}</h2>
              <p className="mt-2 text-sm text-[#64748B]">
                {signUp
                  ? "Start your own private family space in under a minute. You become its admin and can invite relatives."
                  : "Sign in to your family space to add, correct, and share your family."}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                {signUp && (
                  <div className="space-y-1.5">
                    <Label htmlFor="register-name">Your name</Label>
                    <Input id="register-name" name="name" autoComplete="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-11 bg-white" placeholder="e.g. Maria Walters" autoFocus />
                  </div>
                )}
                {signUp ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="register-email">Email</Label>
                    <Input id="register-email" name="email" autoComplete="email" data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" placeholder="you@example.com" />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="login-username">Email or username</Label>
                    <Input id="login-username" name="username" autoComplete="username" data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} className="h-11 bg-white" placeholder="you@example.com" autoFocus />
                  </div>
                )}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={signUp ? "register-password" : "login-password"}>Password</Label>
                    {!signUp && (
                      <button type="button" onClick={() => { setMode("forgot"); setPassword(""); }} data-testid="forgot-password-link" className="text-xs font-semibold text-[#082D4A] hover:underline">Forgot password?</button>
                    )}
                  </div>
                  <Input id={signUp ? "register-password" : "login-password"} name="password" autoComplete={signUp ? "new-password" : "current-password"} data-testid={signUp ? "register-password-input" : "login-password-input"} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" placeholder={signUp ? "At least 6 characters" : ""} />
                </div>
                <Button type="submit" disabled={busy} data-testid={signUp ? "register-submit-button" : "login-submit-button"} className="h-11 w-full rounded-full bg-[#D4A017] text-base font-semibold text-[#082D4A] hover:bg-[#B0850F]">
                  {signUp ? <UserPlus size={17} className="mr-2" /> : <LogIn size={17} className="mr-2" />}
                  {busy ? "Please wait…" : signUp ? "Create my family tree" : "Sign in"}
                </Button>
              </form>

              <div className="my-5 flex items-center gap-3 text-xs text-[#94A3B8]">
                <span className="h-px flex-1 bg-[#D8E0E8]" /> or <span className="h-px flex-1 bg-[#D8E0E8]" />
              </div>
              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button
                type="button"
                data-testid="google-signin-button"
                onClick={() => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`; }}
                className="flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-[#D8E0E8] bg-white text-sm font-medium text-[#082D4A] transition-colors hover:bg-[#F4F6F8]"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Continue with Google
              </button>

              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setMode(signUp ? "signin" : "signup")}
                className="mt-6 w-full text-sm font-medium text-[#082D4A] hover:underline"
              >
                {signUp ? "Already have a family space? Sign in" : "New here? Create your family tree"}
              </button>
              </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
